import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { WORKSPACE_THEME } from "./workspaceConfig";

/**
 * Shared simple tab shell for My Desk / Team Hub.
 */
export default function WorkspaceShell({
  eyebrow,
  title,
  subtitle,
  sections,
  accent = WORKSPACE_THEME.primary,
  children,
}) {
  const [params, setParams] = useSearchParams();
  const sectionId = params.get("section") || sections[0]?.id || "";
  const active = sections.find((s) => s.id === sectionId) || sections[0];

  useEffect(() => {
    if (!sections.length) return;
    if (!sections.some((s) => s.id === sectionId)) {
      const next = new URLSearchParams(params);
      next.set("section", sections[0].id);
      setParams(next, { replace: true });
    }
  }, [sections, sectionId, params, setParams]);

  const go = useMemo(
    () => (id) => {
      const next = new URLSearchParams(params);
      next.set("section", id);
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  return (
    <div className="min-h-full bg-gray-50/80">
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 pt-5 pb-0">
        <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: accent }}>
          {eyebrow}
        </p>
        <h1 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight">
          {title}
        </h1>
        {subtitle ? (
          <p className="mt-1 text-sm text-gray-600 max-w-2xl">{subtitle}</p>
        ) : null}

        <nav className="mt-4 flex gap-1 overflow-x-auto" aria-label="Workspace sections">
          {sections.map((s) => {
            const on = active?.id === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => go(s.id)}
                className={`shrink-0 px-4 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 transition ${
                  on
                    ? "border-current bg-gray-50 text-gray-900"
                    : "border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                }`}
                style={on ? { color: accent, borderColor: accent } : undefined}
              >
                {s.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="px-4 sm:px-6 py-5 w-full max-w-none">
        {typeof children === "function" ? children({ active, go }) : children}
      </div>
    </div>
  );
}
